import { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';

/**
 * Données du tableau de bord personnel — `api/app/routers/dashboard.py`.
 *
 * Ordre de lecture (docs/tableau_de_bord/, décisions du 23/10/2026) : ce qui a changé pour
 * l'utilisateur, son portefeuille sur la durée, ce qui arrive à ses titres, ce qui a bougé
 * chez lui, puis le marché en une ligne et le palmarès de la séance.
 *
 * La date de dernière visite est gardée dans le navigateur : elle ne sert qu'à choisir le
 * point de départ des nouveautés. Absente (première visite, autre appareil), l'API part de
 * la séance précédente.
 */

export type Certitude = 'OFFICIEL' | 'ESTIME';

export interface EvenementAVenir {
  date: string;
  type:
    | 'DETACHEMENT'
    | 'PAIEMENT_DIVIDENDE'
    | 'FRACTIONNEMENT'
    | 'AUGMENTATION_CAPITAL'
    | 'AG'
    | 'PUBLICATION'
    | 'MARCHE_FERME';
  symbole: string | null;
  nom: string | null;
  detenu: boolean;
  libelle: string;
  certitude: Certitude;
  fiabilite?: string;
  montant_brut?: number | null;
  rendement_brut_pct?: number | null;
  montant_net_position?: number | null;
  url?: string | null;
  lieu?: string | null;
}

export interface Nouveaute {
  date: string;
  type: 'RAPPORT' | 'AVIS' | 'DIVIDENDE_ANNONCE' | 'DIVIDENDE_CREDITE';
  symbole: string | null;
  nom: string | null;
  libelle: string;
  url: string | null;
  montant?: number;
  montant_brut?: number | null;
}

export interface TitreDuJour {
  symbole: string;
  nom: string | null;
  detenu: boolean;
  cours_cloture: number | null;
  variation_jour_pct: number | null;
  variation_annee_pct: number | null;
  valeur_echange: number | null;
  sans_echange: boolean;
  pourquoi: { libelle: string; url: string | null } | null;
  /** Clôtures depuis le 1er janvier, à l'échelle du jour (fractionnements). */
  serie_annee: number[];
}

export interface DividendesAnnee {
  annee: number;
  encaisse: number;
  annonce_a_venir: number;
}

export interface IndiceLigne {
  nom: string;
  valeur: number | null;
  variation_jour_pct: number | null;
  variation_annee_pct: number | null;
}

export interface LigneMarche {
  date: string | null;
  indices: IndiceLigne[];
  nb_hausse: number | null;
  nb_baisse: number | null;
  nb_inchange: number | null;
  valeur_echangee: number | null;
}

export interface CotationPalmares {
  societe_symbole: string;
  cours_cloture: number | null;
  variation_jour_pct: number | null;
  variation_annee_pct: number | null;
  volume_echange: number | null;
  valeur_echange: number | null;
}

export interface Palmares {
  gainers: CotationPalmares[];
  losers: CotationPalmares[];
  plus_echanges: CotationPalmares[];
}

export interface MetriquesPortefeuille {
  valeur_actuelle: number;
  plus_minus_value_total: number;
  performance_total: number;
  performance_annuel: number;
  day_pl: number;
  day_pl_pct: number;
}

export interface PointIndices {
  date: string;
  [serie: string]: string | number | undefined;
}

export interface DonneesDashboard {
  nouveautes: Nouveaute[];
  nouveautesDepuis: string | null;
  aVenir: EvenementAVenir[];
  mesTitres: TitreDuJour[];
  dividendes: DividendesAnnee | null;
  marche: LigneMarche | null;
  palmares: Palmares;
  portefeuille: MetriquesPortefeuille | null;
  liquidites: number;
  historiqueIndices: PointIndices[];
  graphiques: GraphiquesPortefeuille | null;
}

export interface PointRendement {
  date: string;
  pct: number;
}

export interface Repartition {
  label: string;
  value: number;
  percentage: number;
}

/** Rendement cumulé hors apports contre le Composite, rebasé au même jour (`depuis`). */
export interface GraphiquesPortefeuille {
  courbe: { depuis: string | null; portefeuille: PointRendement[]; composite: PointRendement[] };
  secteurs: Repartition[];
  pays: Repartition[];
}

const CLE_DERNIERE_VISITE = 'kanea_dashboard_derniere_visite';

/** Séries tracées sur le graphe des indices (base 100 commune). */
export const SERIES_INDICES = [
  { nom: 'BRVM COMPOSITE', cle: 'BRVM_C', libelle: 'Composite', couleur: '#2563eb' },
  { nom: 'BRVM 30', cle: 'BRVM_30', libelle: 'BRVM 30', couleur: '#f59e0b' },
  { nom: 'BRVM PRESTIGE', cle: 'BRVM_PRES', libelle: 'Prestige', couleur: '#7c3aed' },
] as const;

interface PointHistorique {
  date_indice: string;
  nom_indice: string;
  valeur: number | null;
}

function lireDerniereVisite(): string | null {
  try {
    return localStorage.getItem(CLE_DERNIERE_VISITE);
  } catch {
    return null;
  }
}

function noterVisite(): void {
  try {
    // Date locale Africa/Abidjan = UTC : la partie date de l'ISO suffit.
    localStorage.setItem(CLE_DERNIERE_VISITE, new Date().toISOString().slice(0, 10));
  } catch {
    /* stockage indisponible (navigation privée) : on repartira de la séance précédente */
  }
}

/** Base 100 commune : première séance où les 3 indices sont cotés (sinon courbes non comparables). */
function base100(points: PointHistorique[]): PointIndices[] {
  const dates = [...new Set(points.map((p) => p.date_indice))].sort();
  const dateBase = dates.find((d) =>
    SERIES_INDICES.every(({ nom }) =>
      points.some((p) => p.date_indice === d && p.nom_indice === nom && p.valeur)
    )
  );
  if (!dateBase) return [];
  const bases = new Map<string, number>();
  points
    .filter((p) => p.date_indice === dateBase && p.valeur)
    .forEach((p) => bases.set(p.nom_indice, p.valeur as number));
  return dates
    .filter((d) => d >= dateBase)
    .map((d) => {
      const ligne: PointIndices = { date: d };
      points
        .filter((p) => p.date_indice === d)
        .forEach((p) => {
          const serie = SERIES_INDICES.find((s) => s.nom === p.nom_indice);
          const base = bases.get(p.nom_indice);
          if (serie && base && p.valeur) {
            ligne[serie.cle] = Math.round((p.valeur / base) * 10000) / 100;
            ligne[`${serie.cle}_abs`] = p.valeur;
          }
        });
      return ligne;
    });
}

export function useDashboard() {
  const { token } = useAuth();
  const [donnees, setDonnees] = useState<DonneesDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let annule = false;
    const headers = { Authorization: `Bearer ${token}` };
    const lire = async <T>(chemin: string): Promise<T | null> => {
      try {
        const r = await fetch(`${API_URL}${chemin}`, { headers });
        return r.ok ? ((await r.json()) as T) : null;
      } catch {
        // Bloc manquant plutôt qu'écran cassé : chaque bloc gère son absence, et l'échec
        // global (marché, palmarès et titres tous indisponibles) affiche un message.
        return null;
      }
    };

    const charger = async () => {
      setLoading(true);
      setError(null);
      const derniereVisite = lireDerniereVisite();
      const [nouv, aVenir, mesTitres, marche, palmares, [actifs, graphiques], solde, historique] =
        await Promise.all([
          lire<{ data: Nouveaute[]; meta: { depuis: string } }>(
            `/v1/dashboard/nouveautes${derniereVisite ? `?depuis=${derniereVisite}` : ''}`
          ),
          lire<{ data: EvenementAVenir[] }>('/v1/dashboard/a-venir'),
          lire<{ data: TitreDuJour[]; meta: { dividendes: DividendesAnnee } }>(
            '/v1/dashboard/mes-titres'
          ),
          lire<{ data: LigneMarche }>('/v1/dashboard/marche'),
          lire<Palmares>('/v1/cotations/movers'),
          // Réservé aux comptes autorisés à gérer un portefeuille : un 403 laisse la carte en mode invitation.
          // Courbe et répartition APRÈS l'analyse : /v1/portfolio/assets peut reconstruire les
          // instantanés, deux appels simultanés le feraient deux fois en parallèle.
          lire<{ metrics: MetriquesPortefeuille | null }>('/v1/portfolio/assets').then(
            async (a) =>
              [
                a,
                a?.metrics
                  ? await lire<{ data: GraphiquesPortefeuille }>('/v1/dashboard/portefeuille')
                  : null,
              ] as const
          ),
          lire<{ balance: number }>('/api/account/balance'),
          lire<PointHistorique[]>('/v1/indices/history?days=30'),
        ]);
      if (annule) return;
      if (!marche && !palmares && !mesTitres) {
        setError(
          'Les données du tableau de bord n’ont pas pu être chargées. Réessayez dans quelques instants.'
        );
      } else {
        noterVisite();
      }
      setDonnees({
        nouveautes: nouv?.data ?? [],
        nouveautesDepuis: nouv?.meta?.depuis ?? null,
        aVenir: aVenir?.data ?? [],
        mesTitres: mesTitres?.data ?? [],
        dividendes: mesTitres?.meta?.dividendes ?? null,
        marche: marche?.data ?? null,
        palmares: {
          gainers: palmares?.gainers ?? [],
          losers: palmares?.losers ?? [],
          plus_echanges: palmares?.plus_echanges ?? [],
        },
        portefeuille: actifs?.metrics ?? null,
        liquidites: solde?.balance ?? 0,
        historiqueIndices: historique ? base100(historique) : [],
        graphiques: graphiques?.data ?? null,
      });
      setLoading(false);
    };

    charger();
    return () => {
      annule = true;
    };
  }, [token]);

  return { donnees, loading, error };
}
